import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { Button, Spinner, TextInput, SimpleCheckbox } from 'components/common'
import { getInventory, updateInventory } from 'utils/api/inventoryApi'
import {
  ERROR_CODES,
  INVENTORY_FORM_FIELDS,
  PAGE_INVENTORY,
  PAGE_NOT_FOUND,
} from 'utils/constants'
import {
  calculateSellableQty,
  convertTo12HourFormat,
  formatDate,
} from 'utils/helpers'
import {
  updateInventorySchema,
  UpdateInventoryType,
} from 'utils/schema/inventorySchema'
const {
  ITEM_NUMBER,
  BARCODE,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  ERP_QTY,
  LAST_QTY_UPDATE,
  OOS_THRESHOLD,
  SELLABLE_QTY,
  STORE_ID,
  STORE_NAME,
  ON_STOCK,
  USE_GLOBAL_THRESHOLD,
  IGNORE_UPDATE_TO_MAGENTO,
  BLOCK_ERP_INVENTORY,
  ALLOW_ERP_WINDOW_UPDATE,
  BLOCK_FRESH_ERP_INVENTORY,
  CREATED_AT,
  UPDATED_AT,
} = INVENTORY_FORM_FIELDS

const UpdateInventoryForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = useForm<any>({
    resolver: yupResolver(updateInventorySchema),
  })

  const [loading, setLoading] = useState<boolean>(false)

  const [threshold, setThreshold] = useState<{
    productThreshold: string
    storeThreshold: string
    useGlobalThreshold: boolean
  } | null>(null)

  const { id } = useParams()

  const navigate = useNavigate()

  //* FORM SUBMIT - to update the inventory
  const onSubmit: SubmitHandler<UpdateInventoryType> = async (data) => {
    const { oosThreshold, useGlobalThreshold, erpQty } = data

    const toastInventory = toast.loading('Updating Inventory...')
    try {
      // Build Request Body
      const updateData: Partial<UpdateInventoryType> = {
        useGlobalThreshold,
        erpQty,
      }

      // Include threshold in body only if global is false
      if (!useGlobalThreshold) {
        updateData.oosThreshold = oosThreshold
      }

      await updateInventory(id as string, updateData)
      toast.success('Inventory Updated Successfully', { id: toastInventory })
      navigate(PAGE_INVENTORY.path)
    } catch (error) {
      if (error instanceof AxiosError) {
        const msg = error?.response?.data?.errors?.msg
        if (msg === ERROR_CODES.INVALID_OOSTHRESHOLD) {
          return toast.error('Invalid OOS Threshold!', { id: toastInventory })
        }
      }
      toast.error('Failed To Update Inventory', { id: toastInventory })
    }
  }

  // to get the inventory details
  const getInventoryData = async (id: string) => {
    setLoading(true)
    try {
      const { data } = await getInventory(id)

      setThreshold({
        productThreshold: data[OOS_THRESHOLD.key],
        storeThreshold: data.store[OOS_THRESHOLD.key],
        useGlobalThreshold: data[USE_GLOBAL_THRESHOLD.key],
      })

      const formData = {
        ...data,
        ...data?.catalogue,
        ...data?.store,
        barcode: data.catalogue?._id,
        storeId: data.store?._id,

        //* OOS Threshold
        [OOS_THRESHOLD.key]: data[USE_GLOBAL_THRESHOLD.key]
          ? data?.store[OOS_THRESHOLD.key]
          : data[OOS_THRESHOLD.key],

        //* Sellable Quantity
        [SELLABLE_QTY.key]: calculateSellableQty(
          data[ERP_QTY.key],
          data[OOS_THRESHOLD.key],
          data?.store[OOS_THRESHOLD.key],
          data[USE_GLOBAL_THRESHOLD.key],
        ),
        //* Created At & Updated At
        [CREATED_AT.key]: convertTo12HourFormat(data[CREATED_AT.key]),
        [UPDATED_AT.key]: convertTo12HourFormat(data[UPDATED_AT.key]),

        //* Is On Stock
        isOnStock:
          typeof data.isOnStock === 'boolean'
            ? data.isOnStock
              ? 'Yes'
              : 'No'
            : 'N/A',

        //* Last Updated
        lastUpdatedOnErp: data?.lastUpdatedOnErp
          ? formatDate(data?.lastUpdatedOnErp, {
              includeTime: true,
            })
          : '',
      }

      reset(formData)
    } catch (error) {
      navigate(PAGE_NOT_FOUND.path)
    }
    setLoading(false)
  }

  // To toggle the OOS Threshold and the OOS Quantity Based on the Global Setting
  const handleGlobalThresholdChange = (e: any) => {
    const useGlobalThreshold = e.target.checked
    const erpQty = getValues(ERP_QTY.key as any)
    const sellableQty = calculateSellableQty(
      erpQty,
      threshold?.productThreshold ?? '',
      threshold?.storeThreshold ?? '',
      useGlobalThreshold,
    )

    if (useGlobalThreshold) {
      setValue(OOS_THRESHOLD.key, threshold?.storeThreshold ?? '')
    } else {
      setValue(OOS_THRESHOLD.key, threshold?.productThreshold ?? '')
    }

    setValue(SELLABLE_QTY.key as any, sellableQty)
  }

  // To initially fetech inventory data
  useEffect(() => {
    if (id) {
      getInventoryData(id)
    }
  }, [])

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <p className="font-bold">
        {watch(ERP_ITEM_DESC.key as any) || 'Item Name'}
      </p>
      <div className="mt-4 rounded-lg border bg-white p-5">
        <div className=" mx-auto max-w-[900px]">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-wrap gap-x-14 gap-y-3">
              <div className="w-64">
                <TextInput
                  register={register}
                  label={STORE_NAME.label}
                  name={STORE_NAME.key}
                  disabled
                />
              </div>
              <div className="w-64">
                <TextInput
                  register={register}
                  name={STORE_ID.key}
                  label={STORE_ID.label}
                  disabled
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ITEM_NUMBER.label}
                  name={ITEM_NUMBER.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  register={register}
                  label={BARCODE.label}
                  name={BARCODE.key}
                  disabled
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={DEPARTMENT.label}
                  name={DEPARTMENT.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SECTION.label}
                  name={SECTION.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={FAMILY.label}
                  name={FAMILY.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SUB_FAMILY.label}
                  name={SUB_FAMILY.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SAP_DEPARTMENT.label}
                  name={SAP_DEPARTMENT.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SAP_SECTION.label}
                  name={SAP_SECTION.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SAP_FAMILY.label}
                  name={SAP_FAMILY.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SAP_SUB_FAMILY.label}
                  name={SAP_SUB_FAMILY.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ERP_ITEM_DESC.label}
                  name={ERP_ITEM_DESC.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ERP_UOM.label}
                  name={ERP_UOM.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={QTY_PER_UOM.label}
                  name={QTY_PER_UOM.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ERP_PACK_INFO.label}
                  name={ERP_PACK_INFO.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ERP_BRAND.label}
                  name={ERP_BRAND.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ONLINE_DESC.label}
                  name={ONLINE_DESC.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ONLINE_PACK_INFO.label}
                  name={ONLINE_PACK_INFO.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={LAST_QTY_UPDATE.label}
                  name={LAST_QTY_UPDATE.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  register={register}
                  label={ERP_QTY.label}
                  name={ERP_QTY.key}
                  error={errors[ERP_QTY.key]?.message as string}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={
                    threshold?.storeThreshold || threshold?.useGlobalThreshold
                      ? watch(USE_GLOBAL_THRESHOLD.key)
                      : false
                  }
                  register={register}
                  label={OOS_THRESHOLD.label}
                  name={OOS_THRESHOLD.key}
                  required
                  error={errors[OOS_THRESHOLD.key]?.message as string}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={SELLABLE_QTY.label}
                  name={SELLABLE_QTY.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={ON_STOCK.label}
                  name={ON_STOCK.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={CREATED_AT.label}
                  name={CREATED_AT.key}
                />
              </div>
              <div className="w-64">
                <TextInput
                  disabled={true}
                  register={register}
                  label={UPDATED_AT.label}
                  name={UPDATED_AT.key}
                />
              </div>
              <div className="w-64">
                {(threshold?.storeThreshold ||
                  threshold?.useGlobalThreshold) && (
                  <SimpleCheckbox
                    label={USE_GLOBAL_THRESHOLD.label}
                    name={USE_GLOBAL_THRESHOLD.key}
                    register={register}
                    handleChange={handleGlobalThresholdChange}
                  />
                )}
                <div>
                  <SimpleCheckbox
                    label={IGNORE_UPDATE_TO_MAGENTO.label}
                    name={IGNORE_UPDATE_TO_MAGENTO.key}
                    disabled
                    register={register}
                  />
                </div>
                <div>
                  <SimpleCheckbox
                    label={BLOCK_ERP_INVENTORY.label}
                    name={BLOCK_ERP_INVENTORY.key}
                    disabled
                    register={register}
                  />
                </div>
                <div>
                  {/* Read-only: set via inventory import or the store-level
                      ERP window update action */}
                  <SimpleCheckbox
                    label={ALLOW_ERP_WINDOW_UPDATE.label}
                    name={ALLOW_ERP_WINDOW_UPDATE.key}
                    disabled
                    register={register}
                  />
                </div>
                <div>
                  <SimpleCheckbox
                    label={BLOCK_FRESH_ERP_INVENTORY.label}
                    name={BLOCK_FRESH_ERP_INVENTORY.key}
                    disabled
                    register={register}
                  />
                </div>
              </div>
            </div>
            <div className="mt-10 flex gap-10">
              <Button buttonText="submit" className="w-40" />
              <Button
                buttonText="cancel"
                type="button"
                className="w-40"
                variant="outline"
                onClick={() => {
                  navigate(-1)
                }}
              />
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

export default UpdateInventoryForm
